import { useState, useEffect, useCallback, useRef } from 'react';

interface SessionInfo {
  name: string;
  label: string;
  openUTC: number;
  closeUTC: number;
  spreadRange: string;
  liquidity: 'High' | 'Medium' | 'Low';
  bestFor: string;
  color: string;
  bgColor: string;
}

const SESSIONS: SessionInfo[] = [
  {
    name: 'Sydney',
    label: 'Asia / Sydney',
    openUTC: 22,
    closeUTC: 6,
    spreadRange: '2.0 - 4.0 pips',
    liquidity: 'Low',
    bestFor: 'Position building',
    color: '#00A6B4',
    bgColor: '#E6FAFC',
  },
  {
    name: 'Tokyo',
    label: 'Asia / Tokyo',
    openUTC: 0,
    closeUTC: 9,
    spreadRange: '1.5 - 3.0 pips',
    liquidity: 'Medium',
    bestFor: 'Range trading',
    color: '#D97706',
    bgColor: '#FFF7E6',
  },
  {
    name: 'London',
    label: 'Europe / London',
    openUTC: 7,
    closeUTC: 16,
    spreadRange: '0.6 - 1.5 pips',
    liquidity: 'High',
    bestFor: 'Breakouts, news trading',
    color: '#10B981',
    bgColor: '#ECFDF5',
  },
  {
    name: 'New York',
    label: 'North America / New York',
    openUTC: 12,
    closeUTC: 21,
    spreadRange: '0.5 - 1.2 pips',
    liquidity: 'High',
    bestFor: 'Trend following',
    color: '#6366F1',
    bgColor: '#EEF2FF',
  },
];

// Rollover happens at 5 PM ET / 21:00 UTC (22:00 UTC during DST)
// We'll show as 21:00 UTC for simplicity
const ROLLOVER_UTC = 21;

function getUTCNow(): Date {
  return new Date(
    Date.UTC(
      new Date().getUTCFullYear(),
      new Date().getUTCMonth(),
      new Date().getUTCDate(),
      new Date().getUTCHours(),
      new Date().getUTCMinutes(),
    ),
  );
}

function getSessionStatus(session: SessionInfo): {
  isOpen: boolean;
  isClosingSoon: boolean;
  isOpeningSoon: boolean;
  progress: number;
  minutesLeft: number;
  minutesUntilOpen: number;
  nextOpenLabel: string;
} {
  const now = getUTCNow();
  const currentHour = now.getUTCHours();
  const currentMinute = now.getUTCMinutes();
  const currentMinuteOfDay = currentHour * 60 + currentMinute;

  const openMinute = session.openUTC * 60;
  const closeMinute = session.closeUTC * 60;

  // Handle sessions that cross midnight
  let isOpen = false;
  let minutesLeft = 0;
  let minutesUntilOpen = 0;
  let progress = 0;

  if (session.closeUTC < session.openUTC) {
    // Crosses midnight (e.g., Sydney 22:00 - 06:00)
    isOpen =
      currentMinuteOfDay >= openMinute || currentMinuteOfDay < closeMinute;
    if (isOpen) {
      const minutesSinceOpen =
        currentMinuteOfDay >= openMinute
          ? currentMinuteOfDay - openMinute
          : currentMinuteOfDay + 24 * 60 - openMinute;
      const totalDuration = (24 * 60 - openMinute) + closeMinute;
      minutesLeft = totalDuration - minutesSinceOpen;
      progress = (minutesSinceOpen / totalDuration) * 100;
    } else {
      minutesUntilOpen = openMinute - currentMinuteOfDay;
    }
  } else {
    // Normal session within same day
    isOpen =
      currentMinuteOfDay >= openMinute &&
      currentMinuteOfDay < closeMinute;
    if (isOpen) {
      const minutesSinceOpen = currentMinuteOfDay - openMinute;
      const totalDuration = closeMinute - openMinute;
      minutesLeft = totalDuration - minutesSinceOpen;
      progress = (minutesSinceOpen / totalDuration) * 100;
    } else if (currentMinuteOfDay < openMinute) {
      minutesUntilOpen = openMinute - currentMinuteOfDay;
    } else {
      minutesUntilOpen = 24 * 60 - currentMinuteOfDay + openMinute;
    }
  }

  const isClosingSoon = isOpen && minutesLeft <= 60;
  const isOpeningSoon = !isOpen && minutesUntilOpen <= 60;

  let nextOpenLabel = '';
  if (!isOpen) {
    const h = Math.floor(minutesUntilOpen / 60);
    const m = minutesUntilOpen % 60;
    if (h > 0) {
      nextOpenLabel = `Opens in ${h}h ${m}m`;
    } else {
      nextOpenLabel = `Opens in ${m}m`;
    }
  }

  return {
    isOpen,
    isClosingSoon,
    isOpeningSoon,
    progress,
    minutesLeft,
    minutesUntilOpen,
    nextOpenLabel,
  };
}

function formatTimeLeft(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function getCurrentCondition(): {
  title: string;
  description: string;
  spreadWarning: string;
  badgeColor: string;
  badgeBg: string;
} {
  const now = getUTCNow();
  const h = now.getUTCHours();

  // Overlap: London-NY 12:00-16:00 (highest liquidity)
  if (h >= 12 && h < 16) {
    return {
      title: 'Peak Liquidity Window',
      description:
        'London and New York sessions overlap. This is the most active trading period with the tightest spreads and highest market depth.',
      spreadWarning: 'Expect spreads up to 40% tighter than off-peak hours.',
      badgeColor: '#10B981',
      badgeBg: '#ECFDF5',
    };
  }
  // London 07:00-16:00
  if (h >= 7 && h < 16) {
    return {
      title: 'European Session Active',
      description:
        'London session dominates. GBP and EUR pairs see the best pricing. EUR/USD and GBP/USD are the most liquid pairs right now.',
      spreadWarning: 'Major pair spreads are competitive. Avoid exotic pairs until NY opens.',
      badgeColor: '#10B981',
      badgeBg: '#ECFDF5',
    };
  }
  // NY 12:00-21:00
  if (h >= 12 && h < 21) {
    return {
      title: 'North American Session Active',
      description:
        'New York session drives USD and CAD pair liquidity. Major US economic releases happen during these hours.',
      spreadWarning: 'US news events can spike spreads temporarily.',
      badgeColor: '#6366F1',
      badgeBg: '#EEF2FF',
    };
  }
  // Tokyo 00:00-09:00
  if (h >= 0 && h < 9) {
    return {
      title: 'Asian Session Active',
      description:
        'Tokyo session. JPY and AUD pairs are most active. Spread widening is common, especially on crosses.',
      spreadWarning: 'Expect 20-50% wider spreads on non-Asian pairs.',
      badgeColor: '#D97706',
      badgeBg: '#FFF7E6',
    };
  }
  // Sydney only
  if (h >= 22 || h < 6) {
    return {
      title: 'Low Liquidity Period',
      description:
        'Only Sydney session is open. Very thin market with wider spreads. Best for position sizing, not active trading.',
      spreadWarning: 'Spreads may double on major pairs. Avoid scalping.',
      badgeColor: '#94A3B8',
      badgeBg: '#F8FAFC',
    };
  }
  // Default (shouldn't reach)
  return {
    title: 'Market Open',
    description: 'Forex market is open 24/5. Trading conditions vary by session.',
    spreadWarning: '',
    badgeColor: '#94A3B8',
    badgeBg: '#F8FAFC',
  };
}

function getOverlapStatus(): {
  overlapName: string;
  isActive: boolean;
} {
  const h = getUTCNow().getUTCHours();
  if (h >= 12 && h < 16) {
    return { overlapName: 'London - New York Overlap', isActive: true };
  }
  if (h >= 7 && h < 9) {
    return { overlapName: 'Tokyo - London Overlap', isActive: true };
  }
  return { overlapName: 'No Session Overlap', isActive: false };
}

function getRolloverInfo() {
  const now = getUTCNow();
  const h = now.getUTCHours();
  const m = now.getUTCMinutes();
  const currentMinutes = h * 60 + m;
  const rolloverMinutes = ROLLOVER_UTC * 60;

  let minutesUntil: number;
  let isPast: boolean;

  if (currentMinutes < rolloverMinutes) {
    minutesUntil = rolloverMinutes - currentMinutes;
    isPast = false;
  } else {
    minutesUntil = 24 * 60 - currentMinutes + rolloverMinutes;
    isPast = true;
  }

  const hoursUntil = Math.floor(minutesUntil / 60);
  const minsLeft = minutesUntil % 60;

  return {
    timeLabel: `${hoursUntil}h ${minsLeft}m`,
    isToday: !isPast,
    isSoon: minutesUntil <= 120,
  };
}

function getLiquidityBadge(liquidity: 'High' | 'Medium' | 'Low') {
  if (liquidity === 'High') {
    return { label: 'High', bg: '#ECFDF5', color: '#10B981' };
  }
  if (liquidity === 'Medium') {
    return { label: 'Medium', bg: '#FFF7E6', color: '#D97706' };
  }
  return { label: 'Low', bg: '#F8FAFC', color: '#94A3B8' };
}

export default function SessionMonitorSection() {
  const [tick, setTick] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(refresh, 30000); // update every 30 seconds
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refresh]);

  const now = getUTCNow();
  const utcTimeStr = now.toISOString().slice(11, 16) + ' UTC';

  const condition = getCurrentCondition();
  const overlap = getOverlapStatus();
  const rollover = getRolloverInfo();

  const gridCols =
    'grid-cols-[1.2fr_0.9fr_1fr_1fr_1fr_0.8fr]';

  return (
    <section className="w-full bg-background-50 py-[56px] md:py-[80px]">
      <div className="w-full max-w-main mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-12">
          <div>
            <h2 className="text-[28px] md:text-[38px] font-[750] leading-[36px] md:leading-[46px] text-[#101828] tracking-[-0.015em]">
              Market Session Monitor
            </h2>
            <p className="mt-1.5 text-[14px] md:text-[15px] leading-[22px] text-[#667085] max-w-[560px]">
              Live trading session status, liquidity levels, and spread
              expectations across global forex markets.
            </p>
          </div>

          <div className="flex items-center gap-3 mt-4 md:mt-0 flex-shrink-0">
            <span className="inline-flex items-center gap-1.5 text-[13px] text-[#94A3B8] font-medium whitespace-nowrap">
              <i className="ri-time-line text-[14px]"></i>
              {utcTimeStr}
            </span>
            <button
              onClick={refresh}
              className="inline-flex items-center gap-1.5 h-[36px] px-4 rounded-full bg-white border border-[#E6EAF0] text-[13px] font-semibold text-[#344054] hover:border-[#D9E2EC] hover:bg-[#F6F8FB] transition-all active:scale-95 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line text-sm"></i>
              Refresh
            </button>
          </div>
        </div>

        {/* Current market condition card */}
        <div className="mb-8 bg-white border border-[#E6EAF0] rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-[#F2F4F8] bg-[#FAFBFC] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: condition.badgeColor }}
              ></div>
              <span className="text-[14px] font-semibold text-[#101828]">
                {condition.title}
              </span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {overlap.isActive && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full leading-none bg-[#ECFDF5] text-[#10B981]">
                  <i className="ri-flashlight-line text-[12px]"></i>
                  {overlap.overlapName}
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full leading-none bg-[#FEF2F2] text-[#EF4444]">
                <i className="ri-alarm-warning-line text-[12px]"></i>
                Rollover in {rollover.timeLabel}
              </span>
            </div>
          </div>

          <div className="px-5 py-4">
            <p className="text-[14px] leading-[22px] text-[#667085]">
              {condition.description}
            </p>
            {condition.spreadWarning && (
              <p className="mt-2 text-[13px] leading-[20px] text-[#D97706] font-medium">
                <i className="ri-information-line mr-1"></i>
                {condition.spreadWarning}
              </p>
            )}
          </div>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block bg-white border border-[#E6EAF0] rounded-2xl overflow-hidden">
          <div
            className={`grid ${gridCols} gap-3 px-5 py-3.5 border-b border-[#F2F4F8] bg-[#FAFBFC]`}
          >
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#94A3B8]">
              Session
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#94A3B8] text-center">
              Status
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#94A3B8] text-center">
              Time Remaining
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#94A3B8] text-center">
              Avg. Spread
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#94A3B8] text-center">
              Best For
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#94A3B8] text-center">
              Liquidity
            </span>
          </div>

          <div className="divide-y divide-[#F2F4F8]">
            {SESSIONS.map((session) => {
              const status = getSessionStatus(session);
              const badge = getLiquidityBadge(session.liquidity);

              let statusBadge: {
                label: string;
                bg: string;
                color: string;
                icon: string;
              };
              if (status.isOpen) {
                statusBadge = {
                  label: 'Open',
                  bg: session.bgColor,
                  color: session.color,
                  icon: 'ri-checkbox-circle-line',
                };
              } else if (status.isOpeningSoon) {
                statusBadge = {
                  label: 'Opening Soon',
                  bg: '#FEF9C3',
                  color: '#CA8A04',
                  icon: 'ri-time-line',
                };
              } else {
                statusBadge = {
                  label: 'Closed',
                  bg: '#F8FAFC',
                  color: '#94A3B8',
                  icon: 'ri-close-circle-line',
                };
              }

              const openTime = `${String(session.openUTC).padStart(2, '0')}:00`;
              const closeTime = `${String(session.closeUTC).padStart(2, '0')}:00`;

              return (
                <div
                  key={session.name}
                  className={`grid ${gridCols} gap-3 px-5 py-3.5 items-center hover:bg-[#FAFBFC] transition-colors`}
                >
                  {/* Session */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2.5">
                      <span
                        className="inline-flex w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: session.color }}
                      ></span>
                      <span className="text-[14px] font-[700] text-[#101828] tracking-tight whitespace-nowrap">
                        {session.name}
                      </span>
                    </div>
                    <span className="text-[11px] text-[#94A3B8] mt-0.5 block">
                      {openTime} - {closeTime} UTC
                    </span>
                  </div>

                  {/* Status */}
                  <div className="flex justify-center">
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full leading-none"
                      style={{
                        backgroundColor: statusBadge.bg,
                        color: statusBadge.color,
                      }}
                    >
                      <i className={`${statusBadge.icon} text-[12px]`}></i>
                      {statusBadge.label}
                    </span>
                  </div>

                  {/* Time remaining */}
                  <div className="flex justify-center">
                    {status.isOpen ? (
                      <div className="flex flex-col items-center w-full max-w-[120px]">
                        <span className="text-[13px] font-[700] text-[#101828] tabular-nums">
                          {formatTimeLeft(status.minutesLeft)}
                        </span>
                        <div className="w-full h-1 bg-[#F2F4F8] rounded-full mt-1">
                          <div
                            className="h-full rounded-full transition-all duration-300"
                            style={{
                              width: `${status.progress}%`,
                              backgroundColor: status.isClosingSoon
                                ? '#EF4444'
                                : session.color,
                            }}
                          ></div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-[13px] font-semibold text-[#94A3B8] tabular-nums">
                        {status.nextOpenLabel}
                      </span>
                    )}
                  </div>

                  {/* Avg spread */}
                  <div className="flex items-center justify-center">
                    <span className="text-[13px] font-[700] text-[#101828]">
                      {session.spreadRange}
                    </span>
                  </div>

                  {/* Best for */}
                  <div className="flex items-center justify-center">
                    <span className="text-[12px] text-[#667085]">
                      {session.bestFor}
                    </span>
                  </div>

                  {/* Liquidity */}
                  <div className="flex justify-center">
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full leading-none"
                      style={{
                        backgroundColor: badge.bg,
                        color: badge.color,
                      }}
                    >
                      <i className="ri-bar-chart-box-line text-[12px]"></i>
                      {badge.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-2">
          {SESSIONS.map((session) => {
            const status = getSessionStatus(session);
            const badge = getLiquidityBadge(session.liquidity);

            let statusBadge: {
              label: string;
              bg: string;
              color: string;
              icon: string;
            };
            if (status.isOpen) {
              statusBadge = {
                label: 'Open',
                bg: session.bgColor,
                color: session.color,
                icon: 'ri-checkbox-circle-line',
              };
            } else if (status.isOpeningSoon) {
              statusBadge = {
                label: 'Soon',
                bg: '#FEF9C3',
                color: '#CA8A04',
                icon: 'ri-time-line',
              };
            } else {
              statusBadge = {
                label: 'Closed',
                bg: '#F8FAFC',
                color: '#94A3B8',
                icon: 'ri-close-circle-line',
              };
            }

            const openTime = `${String(session.openUTC).padStart(2, '0')}:00`;
            const closeTime = `${String(session.closeUTC).padStart(2, '0')}:00`;

            return (
              <div
                key={session.name}
                className="bg-white border border-[#E6EAF0] rounded-xl px-4 py-3.5"
              >
                {/* Top row: name + status */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="inline-flex w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: session.color }}
                    ></span>
                    <span className="text-[14px] font-[700] text-[#101828]">
                      {session.name}
                    </span>
                    <span className="text-[11px] text-[#94A3B8]">
                      {openTime} - {closeTime}
                    </span>
                  </div>
                  <span
                    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full flex-shrink-0 leading-none"
                    style={{
                      backgroundColor: statusBadge.bg,
                      color: statusBadge.color,
                    }}
                  >
                    <i className={`${statusBadge.icon} text-[12px]`}></i>
                    {statusBadge.label}
                  </span>
                </div>

                {/* Progress bar (if open) */}
                {status.isOpen && (
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-[12px] mb-1">
                      <span className="text-[#667085]">Remaining</span>
                      <span className="font-[700] text-[#101828] tabular-nums">
                        {formatTimeLeft(status.minutesLeft)}
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-[#F2F4F8] rounded-full">
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{
                          width: `${status.progress}%`,
                          backgroundColor: status.isClosingSoon
                            ? '#EF4444'
                            : session.color,
                        }}
                      ></div>
                    </div>
                  </div>
                )}

                {!status.isOpen && (
                  <div className="mb-2 text-[12px] text-[#94A3B8]">
                    {status.nextOpenLabel}
                  </div>
                )}

                {/* Metrics */}
                <div className="flex items-center justify-between text-[13px]">
                  <div className="flex items-center gap-1">
                    <span className="text-[#667085]">Spread</span>
                    <span className="font-[700] text-[#101828]">
                      {session.spreadRange}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[#667085]">Liquidity</span>
                    <span
                      className="text-[11px] font-semibold"
                      style={{ color: badge.color }}
                    >
                      {badge.label}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <p className="mt-4 text-[12px] md:text-[13px] leading-[20px] text-[#94A3B8] text-center max-w-[700px] mx-auto">
          Session times shown in UTC. Forex market operates 24 hours a day,
          5 days a week. Spreads widen during session transitions and major
          news events. Rollover occurs at approximately 21:00 UTC — this is
          when swap fees are calculated on open positions.
        </p>
      </div>
    </section>
  );
}